// Peças do painel montadas a partir das grandezas ao vivo + nominal do regulador: o
// mostrador de uma tag e o aviso de faixa aproximada. Compartilhadas pela Visão geral e
// pelo painel do regulador.

import { Info } from "lucide-react";
import type { LiveAnalog, Nominal, NominalKey, UnitPrefs } from "../../lib/api";
import { GAUGES } from "../../lib/gauges";
import { displayFor, formatValue } from "../../lib/units";
import { Gauge } from "./Gauge";

type Reading = Pick<LiveAnalog, "tag" | "description" | "unit" | "base" | "value">;

export function readingMap(analog: Reading[]): Map<string, Reading> {
  return new Map(analog.map((a) => [a.tag, a]));
}

/** Mostrador da tag; sem nominal (regulador sem `meta`), o valor em número. */
export function TagGauge({ tag, readings, nominal, prefs, stale, height }: { tag: string; readings: Map<string, Reading>; nominal: Nominal; prefs: UnitPrefs | undefined; stale: boolean; height?: number }) {
  const def = GAUGES[tag]!;
  const r = readings.get(tag);
  if (!r) return null;
  const d = displayFor(r.base, r.unit, prefs);
  const nom = nominal[def.nominal];
  if (!nom) {
    return (
      <div className="flex min-w-0 flex-col items-center justify-center text-center">
        <p className="truncate text-xs font-medium text-muted">{def.title}</p>
        <p className="mt-2 text-2xl font-semibold">
          {formatValue(r.value, d)}
          <span className="ml-1 text-sm font-normal text-muted">{d.unit}</span>
        </p>
      </div>
    );
  }
  return <Gauge title={def.title} value={r.value} nominal={nom.value} range={def.range} mode={def.mode} display={d} stale={stale} height={height} />;
}

const NOMINAL_LABEL: Record<NominalKey, string> = {
  voltage: "tensão",
  current: "corrente",
  power: "potência",
  fieldVoltage: "tensão de campo",
  fieldCurrent: "corrente de campo",
  speed: "rotação",
};

/** Avisa quando a faixa é o fundo de escala da medição (gateway antigo), não o nominal. */
export function NominalNotice({ nominal }: { nominal: Nominal }) {
  const approx = (Object.keys(NOMINAL_LABEL) as NominalKey[]).filter((k) => nominal[k]?.source === "fundo_escala");
  if (!approx.length) return null;
  return (
    <p className="flex items-start gap-1.5 text-xs text-muted">
      <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <span>
        Faixa de {approx.map((k) => NOMINAL_LABEL[k]).join(", ")} pelo fundo de escala da medição, não pelo nominal da máquina: atualize o gateway
        ou cadastre o nominal do regulador.
      </span>
    </p>
  );
}

export function pctOf(value: number | null | undefined, nominal: number | undefined): number | null {
  if (value === null || value === undefined || !nominal) return null;
  return (value / nominal) * 100;
}
